import * as React from 'react';

export type PendingChangeCallback = (text: string) => void;
export type NullablePendingChangeCallback = (text: string | null) => void;

export function useThrottlePendingChange(
    onSubmit: PendingChangeCallback,
    delay: number | null
): NullablePendingChangeCallback {
    const savedCallback = React.useRef<PendingChangeCallback>();
    const lastText = React.useRef<string | null>(null);
    const timerId = React.useRef<number | null>(null);

    // Remember the latest callback.
    React.useEffect(() => {
        savedCallback.current = onSubmit;

        return (): void => {
            savedCallback.current = undefined;
            lastText.current = null;
            timerId.current && window.clearTimeout(timerId.current);
        };
    }, [onSubmit]);

    // this callback is only invoked after user has stopped typing
    // for the duration set in 'delay' argument.
    function tick(): void {
        savedCallback.current &&
            lastText.current !== null &&
            savedCallback.current(lastText.current);
    }

    const invalidateCallback = React.useCallback<NullablePendingChangeCallback>(
        (pendingText) => {
            lastText.current = pendingText;

            if (delay === null) {
                return;
            }

            // when user types a character and this callback is invoked,
            // we reset the timer so that we don't raise too many events
            // for intermediate strings if user types fast enough.
            timerId.current && window.clearTimeout(timerId.current);
            timerId.current = window.setTimeout(tick, delay);
        },
        [delay]
    );

    return invalidateCallback;
}
